import * as React from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'motion/react';
import { toast } from 'sonner';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  getTourEntry,
  getTours,
  getToursForContext,
  getWelcomeTour,
  tourMatches,
} from '@/constants/tours/tours';
import { useIsMobile } from '@/hooks/use-mobile';
import { markTour, useTourProgress } from '@/lib/tour-progress';
import { endTour, PAGE_TOUR, setTourStep, startTour } from '@/redux/slices/tourSlice';
import { selectPlatform } from '@/redux/slices/platform/platformSlice';
import TourCard from './TourCard';
import TourWelcome from './TourWelcome';
import useTourContext from './useTourContext';
import useTourPrompts from './useTourPrompts';

const PAD = 6; // breathing room around the highlighted element
const FIND_TIMEOUT = 2000;
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function useAvailableTours() {
  const platform = useSelector(selectPlatform);
  const { accessibleSection } = useSelector(state => state.team.data);
  return React.useMemo(
    () => getTours({ platform, accessibleSection }),
    [platform, accessibleSection]
  );
}

// On phones the sidebar lives in a closed sheet, so its steps show as centred cards.
const isSidebarTarget = target =>
  Boolean(target) && (target.startsWith('nav-') || target === 'search' || target === 'account');

const findElement = step => {
  const selector = step.selector || (step.target && `[data-tour="${step.target}"]`);
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? el : null;
};

const padded = rect => ({
  top: rect.top - PAD,
  left: rect.left - PAD,
  width: rect.width + PAD * 2,
  height: rect.height + PAD * 2,
});

/**
 * Finds the step's element (it may still be loading), scrolls it into view and follows it
 * while the page scrolls or resizes. status: 'finding' | 'found' | 'missing' | 'centre'.
 * The state remembers which step it belongs to, so a new step never reads the last one's.
 */
function useStepTarget(step, enabled) {
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const [state, setState] = React.useState({ step: null, status: 'finding', rect: null });

  React.useEffect(() => {
    if (!enabled || !step) return undefined;
    if ((!step.selector && !step.target) || (isMobile && isSidebarTarget(step.target))) {
      setState({ step, status: 'centre', rect: null });
      return undefined;
    }

    let el = null;
    let frame = 0;
    let observer = null;
    let poll = 0;
    const started = Date.now();

    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!el?.isConnected) return;
        setState({ step, status: 'found', rect: padded(el.getBoundingClientRect()) });
      });
    };

    const attach = found => {
      el = found;
      const rect = el.getBoundingClientRect();
      const offscreen = rect.top < 64 || rect.bottom > window.innerHeight - 16;
      if (offscreen) {
        el.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      measure();
      window.addEventListener('scroll', measure, true);
      window.addEventListener('resize', measure);
      observer = new ResizeObserver(measure);
      observer.observe(el);
    };

    const now = findElement(step);
    if (now) {
      attach(now);
    } else {
      // Pages render their data a moment after the route changes, so look again for a while.
      poll = setInterval(() => {
        const found = findElement(step);
        if (found) {
          clearInterval(poll);
          attach(found);
        } else if (Date.now() - started > FIND_TIMEOUT) {
          clearInterval(poll);
          setState({ step, status: 'missing', rect: null });
        }
      }, 100);
    }

    // The sidebar rail animates its width; re-measure briefly so the ring settles on it.
    const settle = setInterval(measure, 250);
    const stopSettle = setTimeout(() => clearInterval(settle), 1200);

    return () => {
      clearInterval(poll);
      clearInterval(settle);
      clearTimeout(stopSettle);
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [step, enabled, reduceMotion, isMobile]);

  // Until this step has its own result it is still being found; keep the last ring meanwhile,
  // so the cut-out slides to the next element instead of blinking out.
  if (state.step !== step) return { status: 'finding', rect: state.rect };
  return state;
}

/** Dims the page with a cut-out round the element; clicks on the page are blocked. */
function Spotlight({ rect, maskId }) {
  return (
    <div className="fixed inset-0 z-50 animate-in fade-in-0 duration-200 ease-out-quad" aria-hidden>
      <svg className="absolute inset-0 size-full">
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="16"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" className="fill-black/40" mask={`url(#${maskId})`} />
      </svg>
      {rect && (
        <div
          className="absolute start-0 top-0 rounded-lg ring-2 ring-primary transition-transform duration-300 ease-out-expo motion-reduce:transition-none"
          style={{
            width: rect.width,
            height: rect.height,
            transform: `translate3d(${rect.left}px, ${rect.top}px, 0)`,
          }}
        />
      )}
    </div>
  );
}

/**
 * Runs the guided tour in `state.tour`: moves to the step's page, highlights its element and
 * shows the step card beside it (or centred when the step has no element). Also shows the
 * first-run welcome and the "new here?" prompts. Mounted once, in AuthPageLayout.
 */
export default function TourHost() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const context = useTourContext();
  const { pathname } = context;
  const isMobile = useIsMobile();
  const maskId = `tour-mask-${React.useId().replace(/:/g, '')}`;
  const { activeTourId, stepIndex } = useSelector(state => state.tour);
  const isAccessibleSectionLoaded = useSelector(state => state.team.data.isAccessibleSectionLoaded);
  const tours = useAvailableTours();
  const { userKey, progress } = useTourProgress();
  const ready = Boolean(userKey) && isAccessibleSectionLoaded;

  useTourPrompts({ tours, progress, userKey, ready, context });

  // Resolve the menu aliases once the tours for this team are known.
  React.useEffect(() => {
    if (!activeTourId?.startsWith('@') || !ready) return;
    if (activeTourId === PAGE_TOUR) {
      const pageTour = getToursForContext(tours, context)[0];
      if (!pageTour) toast('This page doesn’t have a tour yet');
      dispatch(pageTour ? startTour(pageTour.id) : endTour());
      return;
    }
    const welcome = getWelcomeTour(tours);
    dispatch(welcome ? startTour(welcome.id) : endTour());
  }, [activeTourId, ready, tours, context, dispatch]);

  const tour = tours.find(t => t.id === activeTourId);
  const steps = React.useMemo(() => tour?.steps ?? [], [tour]);
  const step = steps[stepIndex];

  // Give focus back to whatever started the tour (Help, the palette's page) when it ends.
  const openerRef = React.useRef(null);
  React.useEffect(() => {
    if (!tour) return undefined;
    openerRef.current = document.activeElement;
    return () => {
      const opener = openerRef.current;
      requestAnimationFrame(() => opener?.isConnected && opener.focus?.());
    };
  }, [tour]);

  // Where the tour starts: an area tour's own page; a screen tour runs on the screen you're
  // on, or opens its entry page when started from somewhere else. Fixed for the whole tour.
  const [basePath, setBasePath] = React.useState(null);
  React.useEffect(() => {
    if (!tour) {
      setBasePath(null);
      return;
    }
    const here = tour.match && tourMatches(tour, context);
    setBasePath(here ? context.pathname : getTourEntry(tour) || context.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour]);

  // Each step runs on the page named by the latest `path` up to it.
  const stepPath = React.useMemo(() => {
    if (!tour || !basePath) return null;
    let path = basePath;
    for (let i = 0; i <= stepIndex && i < steps.length; i += 1) {
      if (steps[i].path) path = steps[i].path;
    }
    return path;
  }, [tour, basePath, steps, stepIndex]);

  // Go to the step's page once per step. Leaving it any other way (Back, a link) ends the tour.
  const requestedPathRef = React.useRef(null);
  const arrivedPathRef = React.useRef(null);
  React.useEffect(() => {
    if (!tour || !stepPath) {
      requestedPathRef.current = null;
      arrivedPathRef.current = null;
      return;
    }
    if (pathname === stepPath) {
      arrivedPathRef.current = stepPath;
      return;
    }
    if (requestedPathRef.current !== stepPath) {
      requestedPathRef.current = stepPath;
      arrivedPathRef.current = null;
      navigate(stepPath);
      return;
    }
    if (arrivedPathRef.current === stepPath) dispatch(endTour());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, stepPath, pathname]);

  const onPage = Boolean(stepPath) && pathname === stepPath;
  const { status, rect } = useStepTarget(step, Boolean(tour) && onPage);
  const directionRef = React.useRef(1);
  const isLast = stepIndex === steps.length - 1;

  const finish = React.useCallback(
    outcome => {
      if (!tour) return;
      // A skip never undoes an earlier finish.
      if (outcome === 'completed' || progress.tours[tour.id] !== 'completed') {
        markTour(userKey, tour.id, outcome);
      }
      dispatch(endTour());
    },
    [tour, userKey, progress.tours, dispatch]
  );

  const goTo = React.useCallback(
    index => {
      if (index < 0) return;
      if (index >= steps.length) {
        finish('completed');
        return;
      }
      directionRef.current = index > stepIndex ? 1 : -1;
      dispatch(setTourStep(index));
    },
    [steps.length, stepIndex, finish, dispatch]
  );

  // Optional steps whose element isn't there are passed over in the direction of travel.
  React.useEffect(() => {
    if (status !== 'missing' || !step?.optional) return;
    const next = stepIndex + directionRef.current;
    goTo(next < 0 ? stepIndex + 1 : next);
  }, [status, step, stepIndex, goTo]);

  React.useEffect(() => {
    if (!tour) return undefined;
    const onKey = event => {
      if (event.target.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (event.key === 'Escape') finish('skipped');
      if (event.key === 'ArrowRight') goTo(stepIndex + 1);
      if (event.key === 'ArrowLeft') goTo(stepIndex - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, stepIndex, goTo, finish]);

  const welcome = <TourWelcome tours={tours} progress={progress} userKey={userKey} ready={ready} />;

  if (!tour || !step) return welcome;

  const waiting = !onPage || status === 'finding' || (status === 'missing' && step.optional);
  const centred = status === 'centre' || status === 'missing';

  const card = (
    <TourCard
      step={step}
      body={isMobile && step.phoneBody ? step.phoneBody : step.body}
      index={stepIndex}
      total={steps.length}
      isLast={isLast}
      onBack={() => goTo(stepIndex - 1)}
      onNext={() => goTo(stepIndex + 1)}
      onSkip={() => finish('skipped')}
    />
  );

  return (
    <>
      {welcome}
      {createPortal(
        <>
          <Spotlight rect={centred ? null : rect} maskId={maskId} />
          {/* Kept outside the per-step card, so screen readers hear each new step. */}
          <span role="status" className="sr-only">
            {`Step ${stepIndex + 1} of ${steps.length}: ${step.title}`}
          </span>
          {!waiting && centred && (
            <CentredCard key={stepIndex} label={step.title}>
              {card}
            </CentredCard>
          )}
        </>,
        document.body
      )}
      {!waiting && !centred && rect && (
        <Popover open modal>
          <PopoverAnchor asChild>
            <div
              className="pointer-events-none fixed"
              style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
              aria-hidden
            />
          </PopoverAnchor>
          <PopoverContent
            key={stepIndex}
            side={step.placement || 'bottom'}
            align="start"
            sideOffset={12}
            collisionPadding={16}
            aria-label={step.title}
            onOpenAutoFocus={focusNext}
            onCloseAutoFocus={event => event.preventDefault()}
            onEscapeKeyDown={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            className="w-[min(22rem,calc(100vw-2rem))] gap-0 p-0 shadow-float"
          >
            {card}
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

// Land on the main button, so Enter moves on.
function focusNext(event) {
  event.preventDefault();
  event.currentTarget.querySelector('[data-tour-next]')?.focus();
}

/**
 * Intro and closing steps, and steps whose element isn't on screen (e.g. the sidebar on
 * phones). Tab stays inside the card, like the anchored card's modal popover.
 */
function CentredCard({ label, children }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    ref.current?.querySelector('[data-tour-next]')?.focus();
  }, []);

  const trapTab = event => {
    if (event.key !== 'Tab') return;
    const items = Array.from(ref.current?.querySelectorAll(FOCUSABLE) ?? []);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onKeyDown={trapTab}
        className="w-[min(24rem,100%)] rounded-xl bg-popover text-sm text-popover-foreground shadow-float ring-1 ring-foreground/5 duration-200 ease-out-expo animate-in fade-in-0 zoom-in-95 motion-reduce:zoom-in-100 dark:ring-foreground/10"
      >
        {children}
      </div>
    </div>
  );
}
